/**
 * Página — SST Avançado (PCMSO / PGR / CAT)
 * GridProfile / NeuroSynx Ltda
 *
 * Esqueleto inicial. Complementa o módulo Saúde Ocupacional (ASO/EPI) existente.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Stethoscope, ShieldAlert, FileWarning, Plus, Trash2, Loader2,
  Calendar, HeartPulse, Activity, AlertTriangle, CheckCircle2, Send, X, Edit3,
} from 'lucide-react';
import useClientPagination from '../../hooks/useClientPagination';
import GridPagination from '../GridPagination';
import AskMayaButton from '../AskMayaButton';
import Autocomplete from '../ui/autocomplete';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtDate = (s) => !s ? '—' : String(s).slice(0, 10).split('-').reverse().join('/');

const StatusBadge = ({ s }) => {
  const map = {
    vigente: 'bg-emerald-100 text-emerald-700',
    vencido: 'bg-red-100 text-red-700',
    em_revisao: 'bg-amber-100 text-amber-700',
    substituido: 'bg-slate-100 text-slate-500',
  };
  return <Badge className={map[s] || 'bg-slate-100'}>{s}</Badge>;
};

// =========================================================================
// PCMSO TAB
// =========================================================================
const PCMSOTab = ({ refreshKey, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const empty = { ano_referencia: new Date().getFullYear(), data_emissao: '', data_validade: '', medico_responsavel: '', crm_medico: '', riscos_avaliados: [], exames_obrigatorios: [], observacoes: '' };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [novoRisco, setNovoRisco] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/sst/pcmso`, auth()); setItems(r.data || []); }
    catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const { paginated, pagination, setPage } = useClientPagination(items, 10);

  const salvar = async () => {
    if (!form.medico_responsavel || !form.data_emissao || !form.data_validade) return toast.error('Preencha campos obrigatórios');
    try {
      const payload = { ...form };
      if (editingId) {
        await axios.put(`${API}/api/sst/pcmso/${editingId}`, payload, auth());
        toast.success('PCMSO atualizado');
      } else {
        await axios.post(`${API}/api/sst/pcmso`, payload, auth());
        toast.success('PCMSO criado');
      }
      setOpen(false); setEditingId(null); setForm(empty); load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este PCMSO?')) return;
    try { await axios.delete(`${API}/api/sst/pcmso/${id}`, auth()); toast.success('Excluído'); load(); onChange && onChange(); }
    catch { toast.error('Erro'); }
  };

  const adicionarRisco = () => {
    const r = novoRisco.trim();
    if (!r) return;
    if ((form.riscos_avaliados || []).some(item => item.toLowerCase() === r.toLowerCase()))
      return toast.error('Este risco já foi adicionado');
    setForm({ ...form, riscos_avaliados: [...(form.riscos_avaliados || []), r] });
    setNovoRisco('');
  };

  const editar = (p) => {
    setForm({ ...p });
    setEditingId(p.id);
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-emerald-600" />PCMSO — NR-7</CardTitle>
          <CardDescription>Programa de Controle Médico de Saúde Ocupacional (renovação anual).</CardDescription>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="pcmso-new-btn"><Plus className="h-4 w-4 mr-2" />Novo PCMSO</Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="text-left p-3">Ano</th><th className="text-left p-3">Médico</th><th className="text-left p-3">CRM</th><th className="text-left p-3">Emissão</th><th className="text-left p-3">Validade</th><th className="text-left p-3">Status</th><th className="text-right p-3">Ações</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr> :
               paginated.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-slate-500">Nenhum PCMSO cadastrado.</td></tr> :
               paginated.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50">
                  <td className="p-3 font-medium">{p.ano_referencia}</td>
                  <td className="p-3">{p.medico_responsavel}</td>
                  <td className="p-3">{p.crm_medico || '—'}</td>
                  <td className="p-3">{fmtDate(p.data_emissao)}</td>
                  <td className="p-3">{fmtDate(p.data_validade)}</td>
                  <td className="p-3"><StatusBadge s={p.status} /></td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => editar(p)}><Edit3 className="h-4 w-4 text-blue-600" /></Button><Button size="sm" variant="ghost" onClick={() => excluir(p.id)} data-testid={`pcmso-del-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <GridPagination pagination={pagination} onPageChange={setPage} />
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? 'Editar PCMSO' : 'Novo PCMSO'}</DialogTitle><DialogDescription>NR-7 — vigência anual.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label>Ano de Referência *</Label>
              <div className="flex gap-2">
                <Input value={form.ano_referencia || ''} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); setForm({ ...form, ano_referencia: v ? Number(v) : '' }); }} maxLength={4} placeholder="2026" className="flex-1" />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon"><Calendar className="h-4 w-4" /></Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 16 }, (_, i) => new Date().getFullYear() - 10 + i).map(ano => (
                        <Button key={ano} variant={form.ano_referencia === ano ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, ano_referencia: ano })}>{ano}</Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div><Label>Médico Responsável *</Label><Input value={form.medico_responsavel} onChange={(e) => setForm({ ...form, medico_responsavel: e.target.value })} data-testid="pcmso-medico-input" /></div>
            <div><Label>CRM</Label><Input value={form.crm_medico} onChange={(e) => setForm({ ...form, crm_medico: e.target.value })} /></div>
            <div></div>
            <div><Label>Data Emissão *</Label><Input type="date" value={form.data_emissao} onChange={(e) => setForm({ ...form, data_emissao: e.target.value })} /></div>
            <div><Label>Data Validade *</Label><Input type="date" value={form.data_validade} onChange={(e) => setForm({ ...form, data_validade: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Riscos Avaliados</Label>
              <div className="flex flex-wrap gap-1 mb-2">
                {(form.riscos_avaliados || []).map((r, i) => (
                  <Badge key={i} variant="secondary" className="gap-1 pr-1">
                    {r}
                    <button onClick={() => setForm({ ...form, riscos_avaliados: form.riscos_avaliados.filter((_, j) => j !== i) })}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={novoRisco} onChange={e => setNovoRisco(e.target.value)} placeholder="Digite um risco..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarRisco(); } }} />
                <Button variant="outline" size="sm" onClick={adicionarRisco} type="button">Adicionar</Button>
              </div>
            </div>
            <div className="col-span-2"><Label>Observações</Label><Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={salvar} data-testid="pcmso-save-btn"><CheckCircle2 className="h-4 w-4 mr-2" />{editingId ? 'Atualizar' : 'Salvar'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// =========================================================================
// PGR TAB
// =========================================================================
const PGRTab = ({ refreshKey, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const empty = { data_emissao: '', proxima_revisao: '', responsavel_tecnico: '', qualificacao: '', inventario_riscos: [], plano_acao: [] };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/sst/pgr`, auth()); setItems(r.data || []); }
    catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const { paginated, pagination, setPage } = useClientPagination(items, 10);

  const salvar = async () => {
    if (!form.responsavel_tecnico || !form.data_emissao) return toast.error('Preencha campos obrigatórios');
    try {
      if (editingId) {
        await axios.put(`${API}/api/sst/pgr/${editingId}`, form, auth());
        toast.success('PGR atualizado');
      } else {
        await axios.post(`${API}/api/sst/pgr`, form, auth());
        toast.success('PGR criado');
      }
      setOpen(false); setEditingId(null); setForm(empty); load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este PGR?')) return;
    try { await axios.delete(`${API}/api/sst/pgr/${id}`, auth()); toast.success('Excluído'); load(); onChange && onChange(); }
    catch { toast.error('Erro'); }
  };

  const editar = (p) => {
    setForm({ ...p });
    setEditingId(p.id);
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" />PGR — NR-1</CardTitle>
          <CardDescription>Programa de Gerenciamento de Riscos (substituiu o PPRA em 2022).</CardDescription>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="pgr-new-btn"><Plus className="h-4 w-4 mr-2" />Novo PGR</Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="text-left p-3">Responsável Técnico</th><th className="text-left p-3">Qualificação</th><th className="text-left p-3">Emissão</th><th className="text-left p-3">Próxima Revisão</th><th className="text-left p-3">Status</th><th className="text-right p-3">Ações</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr> :
               paginated.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-slate-500">Nenhum PGR cadastrado.</td></tr> :
               paginated.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50">
                  <td className="p-3 font-medium">{p.responsavel_tecnico}</td>
                  <td className="p-3">{p.qualificacao || '—'}</td>
                  <td className="p-3">{fmtDate(p.data_emissao)}</td>
                  <td className="p-3">{fmtDate(p.proxima_revisao)}</td>
                  <td className="p-3"><StatusBadge s={p.status} /></td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => editar(p)}><Edit3 className="h-4 w-4 text-blue-600" /></Button><Button size="sm" variant="ghost" onClick={() => excluir(p.id)} data-testid={`pgr-del-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <GridPagination pagination={pagination} onPageChange={setPage} />
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? 'Editar PGR' : 'Novo PGR'}</DialogTitle><DialogDescription>NR-1 — programa de gerenciamento de riscos.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label>Responsável Técnico *</Label><Input value={form.responsavel_tecnico} onChange={(e) => setForm({ ...form, responsavel_tecnico: e.target.value })} data-testid="pgr-resp-input" /></div>
            <div><Label>Qualificação</Label><Input value={form.qualificacao} onChange={(e) => setForm({ ...form, qualificacao: e.target.value })} placeholder="Eng. Seg. Trabalho" /></div>
            <div><Label>Data Emissão *</Label><Input type="date" value={form.data_emissao} onChange={(e) => setForm({ ...form, data_emissao: e.target.value })} /></div>
            <div><Label>Próxima Revisão</Label><Input type="date" value={form.proxima_revisao} onChange={(e) => setForm({ ...form, proxima_revisao: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={salvar} data-testid="pgr-save-btn"><CheckCircle2 className="h-4 w-4 mr-2" />{editingId ? 'Atualizar' : 'Salvar'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// =========================================================================
// CAT TAB
// =========================================================================
const CATTab = ({ refreshKey, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const empty = { employee_id: '', tipo: 'inicial', data_acidente: '', local_acidente: '', descricao: '', parte_corpo_atingida: '', natureza_lesao: '', cid: '', afastamento: false, dias_afastamento: 0 };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const a = await axios.get(`${API}/api/sst/cat`, auth());
      setItems(a.data || []);
    } catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const { paginated, pagination, setPage } = useClientPagination(items, 10);

  const salvar = async () => {
    if (!form.employee_id || !form.data_acidente || !form.local_acidente || !form.descricao) return toast.error('Preencha campos obrigatórios');
    try {
      await axios.post(`${API}/api/sst/cat`, form, auth());
      toast.success('CAT registrada'); setOpen(false); setForm(empty); load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const marcarTransm = async (id) => {
    const num = window.prompt('Número da CAT emitido pela Previdência (opcional):') || '';
    try {
      await axios.post(`${API}/api/sst/cat/${id}/marcar-transmitida`, null,
        { ...auth(), params: num ? { numero_cat: num } : {} });
      toast.success('Marcada como transmitida'); load(); onChange && onChange();
    } catch { toast.error('Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta CAT?')) return;
    try { await axios.delete(`${API}/api/sst/cat/${id}`, auth()); toast.success('Excluída'); load(); onChange && onChange(); }
    catch { toast.error('Erro'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2"><FileWarning className="h-5 w-5 text-red-600" />CAT — Acidentes de Trabalho</CardTitle>
          <CardDescription>Comunicação de Acidente de Trabalho (Lei 8213/91 art. 22).</CardDescription>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="cat-new-btn"><Plus className="h-4 w-4 mr-2" />Registrar CAT</Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="text-left p-3">Funcionário</th><th className="text-left p-3">Tipo</th><th className="text-left p-3">Data</th><th className="text-left p-3">CID</th><th className="text-left p-3">Afast.</th><th className="text-left p-3">Nº CAT</th><th className="text-left p-3">Transm.</th><th className="text-right p-3">Ações</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr> :
               paginated.length === 0 ? <tr><td colSpan={8} className="p-6 text-center text-slate-500">Nenhuma CAT registrada.</td></tr> :
               paginated.map((c) => (
                <tr key={c.id} className="border-t hover:bg-slate-50">
                  <td className="p-3 font-medium">{c.employee_nome || '—'}</td>
                  <td className="p-3">{c.tipo}</td>
                  <td className="p-3">{fmtDate(c.data_acidente)}</td>
                  <td className="p-3 font-mono text-[10px]">{c.cid || '—'}</td>
                  <td className="p-3">{c.afastamento ? `${c.dias_afastamento}d` : 'Não'}</td>
                  <td className="p-3 font-mono text-[10px]">{c.numero_cat || '—'}</td>
                  <td className="p-3">{c.transmitida_esocial ? <Badge className="bg-emerald-100 text-emerald-700">Sim</Badge> : <Badge className="bg-amber-100 text-amber-700">Não</Badge>}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {!c.evento_s2210_id && (
                      <Button size="sm" variant="ghost" onClick={async () => {
                        try {
                          const r = await axios.post(`${API}/api/sst/cat/${c.id}/gerar-s2210?ambiente=2`, {}, auth());
                          toast.success(`📤 S-2210 gerado em rascunho · ${r.data.id_esocial}`, { duration: 5000 });
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
                      }} title="Gerar evento eSocial S-2210" data-testid={`cat-s2210-${c.id}`}>
                        <Send className="h-4 w-4 text-indigo-600" />
                      </Button>
                    )}
                    {!c.transmitida_esocial && <Button size="sm" variant="ghost" onClick={() => marcarTransm(c.id)} title="Marcar transmitida" data-testid={`cat-transm-${c.id}`}><Send className="h-4 w-4 text-emerald-600" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => excluir(c.id)} data-testid={`cat-del-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <GridPagination pagination={pagination} onPageChange={setPage} />
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Registrar CAT</DialogTitle><DialogDescription>Comunicação de Acidente de Trabalho.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Funcionário *</Label>
              <Autocomplete
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                endpoint={`${API}/api/employees-paged`}
                searchParam="search"
                getOptionLabel={(item) => `${item.nome}${item.cpf ? ` · ${item.cpf}` : ''}${item.cargo ? ` · ${item.cargo}` : ''}`}
                getOptionValue={(item) => item.id}
                placeholder="Digite o Nome, CPF ou Cargo..."
                extraParams={{ active_only: true, limit: 20 }}
                emptyMessage="Nenhum funcionário encontrado"
              />
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inicial">Inicial</SelectItem>
                  <SelectItem value="reabertura">Reabertura</SelectItem>
                  <SelectItem value="comunicacao_obito">Comunicação de Óbito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Data e hora do acidente *</Label><Input type="datetime-local" value={form.data_acidente} onChange={(e) => setForm({ ...form, data_acidente: e.target.value })} /></div>
            <div className="col-span-2"><Label>Local *</Label><Input value={form.local_acidente} onChange={(e) => setForm({ ...form, local_acidente: e.target.value })} placeholder="Ex: Galpão 3 - setor de pintura" /></div>
            <div className="col-span-2"><Label>Descrição *</Label><Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={3} /></div>
            <div><Label>Parte do corpo</Label><Input value={form.parte_corpo_atingida} onChange={(e) => setForm({ ...form, parte_corpo_atingida: e.target.value })} /></div>
            <div><Label>Natureza da lesão</Label><Input value={form.natureza_lesao} onChange={(e) => setForm({ ...form, natureza_lesao: e.target.value })} /></div>
            <div><Label>CID-10</Label><Input value={form.cid} onChange={(e) => setForm({ ...form, cid: e.target.value })} placeholder="Ex: S62.5" /></div>
            <div>
              <Label>Afastamento?</Label>
              <Select value={form.afastamento ? 'sim' : 'nao'} onValueChange={(v) => setForm({ ...form, afastamento: v === 'sim' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="nao">Não</SelectItem><SelectItem value="sim">Sim</SelectItem></SelectContent>
              </Select>
            </div>
            {form.afastamento && <div><Label>Dias de afastamento</Label><Input type="number" value={form.dias_afastamento} onChange={(e) => setForm({ ...form, dias_afastamento: parseInt(e.target.value) || 0 })} /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={salvar} data-testid="cat-save-btn"><CheckCircle2 className="h-4 w-4 mr-2" />Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// =========================================================================
// PAGE
// =========================================================================
const SSTAvancadoPage = () => {
  const [sub, setSub] = useState('pcmso');
  const [refreshKey, setRefreshKey] = useState(0);
  const [kpis, setKpis] = useState(null);
  const bump = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    axios.get(`${API}/api/sst/kpis`, auth()).then(r => setKpis(r.data)).catch(() => {});
  }, [refreshKey]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="sst-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <HeartPulse className="h-6 w-6 text-red-600" /> SST Avançado — Saúde e Segurança
        </h1>
        <p className="text-sm text-slate-500 mt-1">PCMSO (NR-7), PGR (NR-1) e CAT — complementa o módulo ASO/EPI da Saúde Ocupacional.</p>
        <div className="mt-2">
          <AskMayaButton prompt="Me explique as diferenças entre PCMSO (NR-7), PGR (NR-1) e CAT no GridProfile. Quando devo emitir cada um, qual a periodicidade e o que é obrigatório por lei?" testId="ask-maya-sst" />
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="sst-kpis">
          <Card className="border-l-4 border-emerald-400"><CardContent className="p-3"><p className="text-xs uppercase text-slate-500">PCMSO Vigente</p><p className="text-2xl font-bold text-emerald-700">{kpis.pcmso.vigente}<span className="text-sm text-slate-400">/{kpis.pcmso.total}</span></p></CardContent></Card>
          <Card className="border-l-4 border-amber-400"><CardContent className="p-3"><p className="text-xs uppercase text-slate-500">PGR Vigente</p><p className="text-2xl font-bold text-amber-700">{kpis.pgr.vigente}<span className="text-sm text-slate-400">/{kpis.pgr.total}</span></p></CardContent></Card>
          <Card className="border-l-4 border-red-400"><CardContent className="p-3"><p className="text-xs uppercase text-slate-500">CAT (pendentes)</p><p className="text-2xl font-bold text-red-700">{kpis.cat.pendente_transmissao}<span className="text-sm text-slate-400">/{kpis.cat.total}</span></p></CardContent></Card>
        </div>
      )}

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="grid grid-cols-3 mb-4 w-full md:w-fit">
          <TabsTrigger value="pcmso" data-testid="sst-tab-pcmso"><Stethoscope className="h-4 w-4 mr-1" />PCMSO</TabsTrigger>
          <TabsTrigger value="pgr"   data-testid="sst-tab-pgr"><ShieldAlert className="h-4 w-4 mr-1" />PGR</TabsTrigger>
          <TabsTrigger value="cat"   data-testid="sst-tab-cat"><FileWarning className="h-4 w-4 mr-1" />CAT</TabsTrigger>
        </TabsList>
        <TabsContent value="pcmso"><PCMSOTab refreshKey={refreshKey} onChange={bump} /></TabsContent>
        <TabsContent value="pgr"><PGRTab refreshKey={refreshKey} onChange={bump} /></TabsContent>
        <TabsContent value="cat"><CATTab refreshKey={refreshKey} onChange={bump} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SSTAvancadoPage;
